"use client";

import { useEffect } from "react";

import { segmentEvent } from "@/utils/segmentfunctions";

interface VwoEventData {
  0: string; // Event type ("vA")
  1: string; // Experiment ID
  2: string; // Variation ID
}

type VwoCallback = (data: VwoEventData) => void;

type VwoEventListener = ["onEventReceive", string, VwoCallback];

const FIRED_EXPERIMENT_IDS_KEY = "vwo_experiment_viewed_ids";

function hasFiredExperimentViewed(experimentId: string): boolean {
  try {
    const raw = sessionStorage.getItem(FIRED_EXPERIMENT_IDS_KEY);
    if (!raw) return false;
    const ids: unknown = JSON.parse(raw);
    return Array.isArray(ids) && ids.includes(String(experimentId));
  } catch {
    return false;
  }
}

function markExperimentViewedFired(experimentId: string): void {
  try {
    const id = String(experimentId);
    const raw = sessionStorage.getItem(FIRED_EXPERIMENT_IDS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const ids = Array.isArray(parsed) ? (parsed as string[]) : [];
    if (ids.includes(id)) return;
    sessionStorage.setItem(FIRED_EXPERIMENT_IDS_KEY, JSON.stringify([...ids, id]));
  } catch {
    /* ignore quota / private mode */
  }
}

// Extend Window interface to include VWO types
declare global {
  interface Window {
    VWO?: VwoEventListener[];
    _vwo_exp?: {
      [key: string]: {
        name: string;
        type: string;
        comb_n: { [key: string]: string };
      };
    };
    experimentFn?: () => void;
  }
}

export default function VwoSegmentIntegration(): null {
  useEffect(() => {
    // Initialize VWO array if it doesn't exist
    if (typeof window !== "undefined") {
      window.VWO = window.VWO || [];

      // Add the event listener for VWO events
      window.VWO.push([
        "onEventReceive",
        "vA",
        function (data: VwoEventData) {
          // To fetch A/B test id
          const experimentId = data[1];
          // To fetch A/B test active variation name
          const variationId = data[2];
          // To get A/B test name
          const abTestName = window._vwo_exp?.[experimentId]?.name;
          // To get A/B test active variation name
          const variationName = window._vwo_exp?.[experimentId]?.comb_n?.[variationId];

          if (
            typeof window._vwo_exp?.[experimentId]?.comb_n?.[variationId] !== "undefined" &&
            window._vwo_exp?.[experimentId]?.type &&
            ["VISUAL_AB", "VISUAL", "SPLIT_URL", "SURVEY"].indexOf(
              window._vwo_exp[experimentId].type
            ) > -1
          ) {
            /*
             * Write your logic here to send the data at your end
             * console.log(window._vwo_exp[experimentId])
             * console.log("TestId ", experimentId)
             * console.log("TestName ", abTestName)
             * console.log("VarId ", variationId)
             * console.log("VarName ", variationName)
             */

            if (window.analytics && experimentId && abTestName && variationId && variationName) {
              if (hasFiredExperimentViewed(experimentId)) return;

              markExperimentViewedFired(experimentId);
              segmentEvent("Experiment Viewed", {
                experiment_id: experimentId,
                experiment_name: abTestName,
                variant_name: variationName,
              });
            }
          }
        },
      ]);
    }
  }, []);

  return null;
}
